/**
 * 侧边栏模块注册表（单一数据源）。
 * 新增业务模块：在此追加条目，并在 App.js 注册 Route。
 */
import {
    UserOutlined,
    SafetyCertificateOutlined,
    PlaySquareOutlined,
    PictureOutlined,
    FundOutlined,
    ReadOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import { canManageUsers, isSuperAdmin } from '../utils/role';

/** @typedef {'content'|'finance'|'system'} MenuGroupKey */

/**
 * @typedef {Object} ModuleMenuItem
 * @property {string} key - Menu 选中 key
 * @property {MenuGroupKey} groupKey
 * @property {string} groupLabel
 * @property {string} label
 * @property {string} path - 相对 /admin/ 的路径
 * @property {React.ComponentType} icon
 * @property {(user: object|null) => boolean} visible
 * @property {number} order
 */

/** @type {ModuleMenuItem[]} */
export const MODULE_MENU_ITEMS = [
    {
        key: 'videos',
        groupKey: 'content',
        groupLabel: '内容库',
        label: '视频浏览',
        path: 'videos',
        icon: PlaySquareOutlined,
        visible: () => true,
        order: 10,
    },
    {
        key: 'images',
        groupKey: 'content',
        groupLabel: '内容库',
        label: '图片浏览',
        path: 'images',
        icon: PictureOutlined,
        visible: () => true,
        order: 20,
    },
    {
        key: 'funds',
        groupKey: 'finance',
        groupLabel: '投资洞察',
        label: '我的基金',
        path: 'funds',
        icon: FundOutlined,
        visible: () => true,
        order: 30,
    },
    {
        key: 'news',
        groupKey: 'finance',
        groupLabel: '投资洞察',
        label: '财经资讯',
        path: 'news',
        icon: ReadOutlined,
        visible: () => true,
        order: 40,
    },
    {
        key: 'agent-inbox',
        groupKey: 'finance',
        groupLabel: '投资洞察',
        label: '消息通知',
        path: 'agent-inbox',
        icon: InboxOutlined,
        visible: () => true,
        order: 50,
    },
    {
        key: 'users',
        groupKey: 'system',
        groupLabel: '系统管理',
        label: '用户账号管理',
        path: 'users',
        icon: UserOutlined,
        visible: canManageUsers,
        order: 70,
    },
    {
        key: 'permissions',
        groupKey: 'system',
        groupLabel: '系统管理',
        label: '角色与权限',
        path: 'permissions',
        icon: SafetyCertificateOutlined,
        visible: isSuperAdmin,
        order: 80,
    },
];

/** 分组展示顺序 */
const GROUP_ORDER = ['content', 'finance', 'system'];

/**
 * 根据当前用户构建 Ant Design Menu items（含分组 children）。
 * @param {object|null} user
 * @param {(path: string, label: string) => import('react').ReactNode} linkRenderer
 */
export function buildGroupedMenuItems(user, linkRenderer) {
    const visible = MODULE_MENU_ITEMS.filter((item) => item.visible(user)).sort(
        (a, b) => a.order - b.order
    );

    const groups = new Map();
    for (const item of visible) {
        if (!groups.has(item.groupKey)) {
            groups.set(item.groupKey, {
                key: `group-${item.groupKey}`,
                label: item.groupLabel,
                children: [],
            });
        }
        const Icon = item.icon;
        groups.get(item.groupKey).children.push({
            key: item.key,
            icon: <Icon />,
            label: linkRenderer(item.path, item.label),
        });
    }

    return GROUP_ORDER.filter((gk) => groups.has(gk)).map((gk) => groups.get(gk));
}

/**
 * 根据 pathname 解析当前选中的 menu key（含基金详情页）。
 * @param {string} pathname
 */
export function resolveMenuSelectedKey(pathname) {
    if (pathname.includes('/funds')) {
        return 'funds';
    }
    const sorted = [...MODULE_MENU_ITEMS].sort((a, b) => b.path.length - a.path.length);
    for (const item of sorted) {
        if (pathname.includes(`/${item.path}`)) {
            return item.key;
        }
    }
    return 'videos';
}

/**
 * 根据 pathname 解析应展开的分组 key。
 * @param {string} pathname
 */
export function resolveOpenGroupKeys(pathname) {
    const selected = resolveMenuSelectedKey(pathname);
    const item = MODULE_MENU_ITEMS.find((m) => m.key === selected);
    return item ? [`group-${item.groupKey}`] : [];
}
